import * as React from "react";
import ReactECharts from "echarts-for-react";
import * as echarts from "echarts/core";
import { GeoComponent, TooltipComponent, LegendComponent } from "echarts/components";
import { EffectScatterChart, ScatterChart } from "echarts/charts";
import { CanvasRenderer } from "echarts/renderers";
import type { EChartsOption } from "echarts";
import { Button } from "antd";
import { PlusOutlined, MinusOutlined, AimOutlined } from "@ant-design/icons";
import { Workload } from "../../../../schema/types/workload/entity";
import { Gvc } from "../../../../schema/types/gvc/entity";
import { Deployment } from "../../../../schema/types/workload/deployment";
import { useAppSelector } from "../../../../store/hooks";
import { selectOrg } from "../../../../store/contextSlice";
import { useLocationGeoData, useAllOrgLocations, LocationMarker } from "./useLocationGeoData";
import { loadWorldGeoJson } from "./worldGeoJson";

// Register ECharts components
echarts.use([GeoComponent, TooltipComponent, LegendComponent, EffectScatterChart, ScatterChart, CanvasRenderer]);

// Feature flags for the map
interface FeatureFlags {
  // Show all org locations as gray dots (locations not in GVC shown without labels)
  showAllOrgLocations?: boolean;
}

interface Props {
  workload: Workload;
  gvc?: Gvc;
  deployments: Deployment[];
  features?: FeatureFlags;
}

const mapColors = {
  light: {
    land: "#f0f7f2",
    border: "#c8d9cb",
    text: "#333",
    emphasis: "#e8f0ea",
    ocean: "#e8f4fc",
    inactive: "#b0b0b0",
  },
  dark: {
    land: "#1e2a22",
    border: "#2d3d32",
    text: "#fff",
    emphasis: "#253029",
    ocean: "#141c20",
    inactive: "#666666",
  },
  ready: "#30bd80",
  unhealthy: "#ff4444",
  deploying: "#f5a623",
  pending: "#0084ff", // blue - same as --color-primary
};

type DeploymentState = "ready" | "updating" | "deploying" | "error" | "pending" | "inactive";

function getDeploymentState(marker: LocationMarker): DeploymentState {
  if (marker.isInactive) return "inactive";
  if (marker.isPending) return "pending";

  // Ready: overall ready AND latest version ready AND matches expected
  if (
    marker.isReady &&
    marker.latestVersionReady &&
    marker.latestVersionNumber === marker.expectedVersion
  ) {
    return "ready";
  }

  // Updating: overall ready but latest version not ready (serving old version)
  if (marker.isReady && !marker.latestVersionReady) {
    return "updating";
  }

  // Deploying: not ready but actively progressing
  if (!marker.isReady && (marker.isInitializing || marker.isProgressing)) {
    return "deploying";
  }

  // Error: not ready and not progressing
  return "error";
}

const stateColors: Record<DeploymentState, string> = {
  ready: mapColors.ready,
  updating: mapColors.pending,
  deploying: mapColors.deploying,
  error: mapColors.unhealthy,
  pending: mapColors.pending,
  inactive: mapColors.light.inactive, // Will be overridden by theme
};

const stateLabels: Record<DeploymentState, string> = {
  ready: "Ready",
  updating: "Updating",
  deploying: "Deploying",
  error: "Error",
  pending: "Pending",
  inactive: "Not enabled",
};

export const WorldMapPanel: React.FC<Props> = ({ workload, gvc, deployments, features = {} }) => {
  const theme = useAppSelector((state) => state.theme.theme);
  const org = useAppSelector(selectOrg);
  const [mapRegistered, setMapRegistered] = React.useState(false);
  const [mapError, setMapError] = React.useState<string | null>(null);
  const chartRef = React.useRef<ReactECharts>(null);

  // Track whether initial zoom has been applied to preserve user's pan/zoom state
  const [isInitialZoomApplied, setIsInitialZoomApplied] = React.useState(false);
  // Store initial view for reset functionality
  const [initialView, setInitialView] = React.useState<{ zoom: number; center: [number, number] } | null>(null);

  const { markers, error: markersError } = useLocationGeoData(deployments, org);

  // Always fetch org locations (needed for GVC location geo data)
  const { locations: allOrgLocations, error: allLocationsError } = useAllOrgLocations(org, true);

  // Load and register the world map
  React.useEffect(() => {
    let cancelled = false;

    async function registerMap() {
      try {
        // Check if map is already registered
        if (echarts.getMap("world")) {
          setMapRegistered(true);
          return;
        }

        const geoJson = await loadWorldGeoJson();
        if (cancelled) return;

        echarts.registerMap("world", geoJson);
        setMapRegistered(true);
      } catch (err) {
        if (!cancelled) {
          setMapError(err instanceof Error ? err.message : "Failed to load map");
        }
      }
    }

    registerMap();

    return () => {
      cancelled = true;
    };
  }, []);

  const colors = theme === "dark" ? mapColors.dark : mapColors.light;

  // Get GVC location names from gvc spec
  const gvcLocationNames = React.useMemo(() => {
    if (!gvc?.spec?.staticPlacement?.locationLinks) return new Set<string>();
    return new Set(gvc.spec.staticPlacement.locationLinks.map((link) => link.split("/").pop()!));
  }, [gvc]);

  // Pending locations = in GVC but no deployment info yet (blue)
  const pendingLocations = React.useMemo(() => {
    const deployedNames = new Set(markers.map((m) => m.name));
    return allOrgLocations.filter((loc) => gvcLocationNames.has(loc.name) && !deployedNames.has(loc.name));
  }, [markers, allOrgLocations, gvcLocationNames]);

  // Inactive locations = in org but NOT in GVC (gray, only when feature enabled)
  const inactiveLocations = React.useMemo(() => {
    if (!features.showAllOrgLocations) return [];
    const deployedNames = new Set(markers.map((m) => m.name));
    return allOrgLocations.filter((loc) => !gvcLocationNames.has(loc.name) && !deployedNames.has(loc.name));
  }, [features.showAllOrgLocations, markers, allOrgLocations, gvcLocationNames]);

  // Compute zoom and center based on markers + pending locations
  const computedView = React.useMemo(() => {
    // Include both active markers and pending locations for zoom calculation
    const allRelevantLocations = [...markers, ...pendingLocations];

    let center: [number, number] = [0, 20];
    let zoom = 1.2;

    if (allRelevantLocations.length > 0) {
      const lons = allRelevantLocations.map((m) => m.lon);
      const lats = allRelevantLocations.map((m) => m.lat);
      const minLon = Math.min(...lons);
      const maxLon = Math.max(...lons);
      const minLat = Math.min(...lats);
      const maxLat = Math.max(...lats);

      // Center point
      center = [(minLon + maxLon) / 2, (minLat + maxLat) / 2];

      // Calculate zoom based on span (with padding)
      const lonSpan = Math.max(maxLon - minLon, 10); // minimum 10 degrees
      const latSpan = Math.max(maxLat - minLat, 10);
      const maxSpan = Math.max(lonSpan, latSpan);

      // Approximate zoom calculation (360 degrees = zoom 1)
      // Add padding by increasing the span
      const paddedSpan = maxSpan * 1.5;
      zoom = Math.min(8, Math.max(1.5, 180 / paddedSpan));
    }

    return { zoom, center };
  }, [markers, pendingLocations]);

  // After first render with markers or pending locations, mark as initialized and store initial view
  React.useEffect(() => {
    const hasLocations = markers.length > 0 || pendingLocations.length > 0;
    if (hasLocations && !isInitialZoomApplied) {
      setIsInitialZoomApplied(true);
      setInitialView(computedView);
    }
  }, [markers.length, pendingLocations.length, isInitialZoomApplied, computedView]);

  // Build a lookup from location name to its local endpoint URL
  const endpointByLocation = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const dep of deployments) {
      if (dep.name && dep.status?.endpoint) {
        map.set(dep.name, dep.status.endpoint);
      }
    }
    return map;
  }, [deployments]);

  const option: EChartsOption = React.useMemo(() => {
    if (!mapRegistered) return {};

    const getMarkerColor = (marker: LocationMarker) => {
      const state = getDeploymentState(marker);
      if (state === "inactive") {
        return colors.inactive;
      }
      return stateColors[state];
    };

    const series: any[] = [];

    // Inactive locations (gray dots, no labels) - render first so they're behind active markers
    if (features.showAllOrgLocations && inactiveLocations.length > 0) {
      series.push({
        type: "scatter",
        coordinateSystem: "geo",
        data: inactiveLocations.map((m) => ({
          name: m.name,
          value: [m.lon, m.lat],
          marker: { ...m, isInactive: true },
          itemStyle: {
            color: colors.inactive,
          },
        })),
        symbolSize: 8,
        label: {
          show: false,
        },
        zlevel: 0,
      });
    }

    // Pending locations (blue dots, no labels) - GVC locations without deployment data
    if (pendingLocations.length > 0) {
      series.push({
        type: "scatter",
        coordinateSystem: "geo",
        data: pendingLocations.map((m) => ({
          name: m.name,
          value: [m.lon, m.lat],
          marker: { ...m, isPending: true },
          itemStyle: {
            color: mapColors.pending,
          },
        })),
        symbolSize: 8,
        label: {
          show: false,
        },
        zlevel: 0,
      });
    }

    // Active locations (static dots with labels)
    series.push({
      type: "scatter",
      coordinateSystem: "geo",
      data: markers.map((m) => ({
        name: m.name,
        value: [m.lon, m.lat],
        marker: m,
        itemStyle: {
          color: getMarkerColor(m),
        },
        cursor: getDeploymentState(m) === "ready" && endpointByLocation.has(m.name) ? "pointer" : "default",
      })),
      symbolSize: (val: number[], params: any) => {
        const marker = params.data?.marker as LocationMarker;
        if (!marker) return 12;
        return Math.max(12, Math.min(28, 10 + marker.replicaCount * 4));
      },
      labelLayout: {
        hideOverlap: true,
      },
      label: {
        show: true,
        formatter: "{b}",
        position: "bottom",
        fontSize: 11,
        color: colors.text,
        distance: 5,
      },
      zlevel: 1,
    });

    // Animated ripple effect for ready locations
    series.push({
      type: "effectScatter",
      coordinateSystem: "geo",
      data: markers
        .filter((m) => getDeploymentState(m) === "ready" && m.replicaCount > 0)
        .map((m) => ({
          name: m.name,
          value: [m.lon, m.lat],
          marker: m,
          itemStyle: {
            color: mapColors.ready,
          },
          cursor: endpointByLocation.has(m.name) ? "pointer" : "default",
        })),
      symbolSize: (val: number[], params: any) => {
        const marker = params.data?.marker as LocationMarker;
        if (!marker) return 12;
        return Math.max(12, Math.min(28, 10 + marker.replicaCount * 4));
      },
      showEffectOn: "render",
      rippleEffect: {
        brushType: "stroke",
        scale: 3,
        period: 4,
      },
      label: {
        show: false,
      },
      zlevel: 2,
    });

    // Build geo config - only include zoom/center on first render
    const geoConfig: any = {
      map: "world",
      roam: true,
      scaleLimit: {
        min: 1,
        max: 10,
      },
      itemStyle: {
        areaColor: colors.land,
        borderColor: colors.border,
        borderWidth: 0.5,
      },
      emphasis: {
        disabled: true,
      },
      silent: true,
    };

    // Only set initial zoom/center once to preserve user's pan/zoom state
    if (!isInitialZoomApplied) {
      geoConfig.zoom = computedView.zoom;
      geoConfig.center = computedView.center;
    }

    return {
      backgroundColor: colors.ocean,
      tooltip: {
        trigger: "item",
        formatter: (params: any) => {
          if (!params.data) return "";
          const marker = params.data.marker as LocationMarker;

          if (marker.isInactive) {
            let html = `<div style="font-family: var(--font-family-primary);">`;
            html += `<div style="font-weight: 600; margin-bottom: 4px;">${marker.name}</div>`;
            if (marker.city || marker.country) {
              const location = [marker.city, marker.country].filter(Boolean).join(", ");
              html += `<div style="color: #888; font-size: 12px; margin-bottom: 4px;">${location}</div>`;
            }
            html += `<div style="color: #888; font-size: 12px;">Not enabled in GVC</div>`;
            html += `</div>`;
            return html;
          }

          if (marker.isPending) {
            let html = `<div style="font-family: var(--font-family-primary);">`;
            html += `<div style="font-weight: 600; margin-bottom: 4px;">${marker.name}</div>`;
            if (marker.city || marker.country) {
              const location = [marker.city, marker.country].filter(Boolean).join(", ");
              html += `<div style="color: #888; font-size: 12px; margin-bottom: 4px;">${location}</div>`;
            }
            html += `<div style="display: flex; align-items: center; gap: 6px;">`;
            html += `<span style="width: 8px; height: 8px; border-radius: 50%; background: ${mapColors.pending};"></span>`;
            html += `<span>Pending</span>`;
            html += `</div>`;
            html += `</div>`;
            return html;
          }

          const state = getDeploymentState(marker);
          const statusLabel = stateLabels[state];
          const statusColor = getMarkerColor(marker);

          let html = `<div style="font-family: var(--font-family-primary);">`;
          html += `<div style="font-weight: 600; margin-bottom: 4px;">${marker.name}</div>`;
          if (marker.city || marker.country) {
            const location = [marker.city, marker.country].filter(Boolean).join(", ");
            html += `<div style="color: #888; font-size: 12px; margin-bottom: 4px;">${location}</div>`;
          }
          html += `<div style="display: flex; align-items: center; gap: 6px;">`;
          html += `<span style="width: 8px; height: 8px; border-radius: 50%; background: ${statusColor};"></span>`;
          html += `<span>${statusLabel}</span>`;
          html += `</div>`;
          html += `<div style="margin-top: 4px;">Replicas: ${marker.replicaCount}</div>`;
          if (state === "ready" && endpointByLocation.has(marker.name)) {
            html += `<div style="margin-top: 6px; color: #888; font-size: 11px;">Click to open endpoint</div>`;
          }
          html += `</div>`;
          return html;
        },
      },
      geo: geoConfig,
      series,
    };
  }, [
    mapRegistered,
    markers,
    pendingLocations,
    inactiveLocations,
    colors,
    theme,
    features.showAllOrgLocations,
    isInitialZoomApplied,
    computedView,
    endpointByLocation,
  ]);

  // Event handlers for zoom controls
  const handleZoomIn = React.useCallback(() => {
    const instance = chartRef.current?.getEchartsInstance();
    if (instance) {
      const opt = instance.getOption() as any;
      const currentZoom = opt.geo?.[0]?.zoom ?? 1.2;
      instance.setOption({ geo: { zoom: Math.min(10, currentZoom * 1.5) } });
    }
  }, []);

  const handleZoomOut = React.useCallback(() => {
    const instance = chartRef.current?.getEchartsInstance();
    if (instance) {
      const opt = instance.getOption() as any;
      const currentZoom = opt.geo?.[0]?.zoom ?? 1.2;
      instance.setOption({ geo: { zoom: Math.max(1, currentZoom / 1.5) } });
    }
  }, []);

  const handleReset = React.useCallback(() => {
    const instance = chartRef.current?.getEchartsInstance();
    if (instance && initialView) {
      instance.setOption({ geo: { zoom: initialView.zoom, center: initialView.center } });
    }
  }, [initialView]);

  // Click handler for opening location endpoints
  const handleMarkerClick = React.useCallback(
    (params: any) => {
      const marker = params.data?.marker as LocationMarker | undefined;
      if (!marker) return;
      if (getDeploymentState(marker) !== "ready") return;
      const url = endpointByLocation.get(marker.name);
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    },
    [endpointByLocation]
  );

  // Event handler for georoam to force label re-evaluation on zoom
  const onEvents = React.useMemo(
    () => ({
      click: handleMarkerClick,
      georoam: () => {
        const instance = chartRef.current?.getEchartsInstance();
        if (instance) {
          // Force label layout recalculation
          instance.setOption({}, { lazyUpdate: false });
        }
      },
    }),
    [handleMarkerClick]
  );

  const error = mapError || markersError || allLocationsError;

  if (error || !mapRegistered) {
    return null;
  }

  return (
    <div style={{ position: "relative" }}>
      <ReactECharts
        ref={chartRef}
        option={option}
        style={{ height: 350, width: 700, borderRadius: 6, overflow: "hidden" }}
        opts={{ renderer: "canvas" }}
        notMerge={false}
        onEvents={onEvents}
      />
      <div
        style={{
          position: "absolute",
          bottom: 12,
          left: 12,
          display: "flex",
          flexDirection: "column",
          gap: 4,
        }}
      >
        <Button size="small" icon={<PlusOutlined />} onClick={handleZoomIn} />
        <Button size="small" icon={<MinusOutlined />} onClick={handleZoomOut} />
        <Button size="small" icon={<AimOutlined />} onClick={handleReset} />
      </div>
    </div>
  );
};
